import React, { useCallback, useEffect, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Full-screen view of one photo, uncropped. Esc or a click outside the photo closes it;
// the arrows (and arrow keys) step through the rest of the set.
function PhotoLightbox({ photos, index, onClose, onChange }) {
  const closeRef = useRef(null);
  const open = index !== null && index >= 0;
  const count = photos.length;

  const step = useCallback(
    (delta) => onChange((index + delta + count) % count),
    [index, count, onChange]
  );

  // Latest handlers, so the listeners below only attach once per open
  const keys = useRef({});
  keys.current = { onClose, step };

  useEffect(() => {
    if (!open) return undefined;
    const returnFocusTo = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") keys.current.onClose();
      else if (e.key === "ArrowRight") keys.current.step(1);
      else if (e.key === "ArrowLeft") keys.current.step(-1);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      returnFocusTo?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const photo = photos[index];
  const navButton =
    "absolute top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-10"
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        aria-label="Close photo"
        onClick={onClose}
        className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors"
      >
        <CloseIcon />
      </button>

      <figure className="flex max-h-full max-w-full flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.src}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          className="max-h-[calc(100vh-7rem)] max-w-full w-auto h-auto rounded-lg object-contain shadow-2xl"
        />
        <figcaption className="pt-3 text-center text-base font-semibold text-white">
          {photo.caption}
          {count > 1 && (
            <span className="pl-3 text-sm font-normal text-white/60">
              {index + 1} / {count}
            </span>
          )}
        </figcaption>
      </figure>

      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            className={`${navButton} left-2 sm:left-4`}
          >
            <ChevronLeftIcon fontSize="large" />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            className={`${navButton} right-2 sm:right-4`}
          >
            <ChevronRightIcon fontSize="large" />
          </button>
        </>
      )}
    </div>
  );
}

export default PhotoLightbox;
